import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adsListBrands, adsGenerateIntelligence, adsGetIntelligence } from "../services/api";

// ADS Intelligence Agent UI - generate + view competitive-intelligence reports
// synthesized from creatives' visual attributes (ADS_AGENT_PLAN §5).

const GOLD = "#d4af37";
const INK = "#0b1020";
const PANEL = "#11162a";

function Bar({ pct }) {
  return (
    <span style={{ display: "inline-block", width: 60, height: 6, background: "#1e2440", borderRadius: 3, overflow: "hidden", verticalAlign: "middle" }}>
      <span style={{ display: "block", width: `${Math.min(100, pct)}%`, height: "100%", background: GOLD }} />
    </span>
  );
}

function DistTable({ title, dist }) {
  const vals = (dist && dist.values) || {};
  const entries = Object.entries(vals);
  if (!entries.length) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: "#8a90a6", textTransform: "uppercase", marginBottom: 2 }}>{title}</div>
      {entries.slice(0, 6).map(([name, m]) => (
        <div key={name} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, padding: "1px 0" }}>
          <span style={{ minWidth: 130, color: "#cdd2e6" }}>{name}</span>
          <Bar pct={m.pct} />
          <span style={{ color: "#8a90a6" }}>{m.pct}% ({m.count})</span>
        </div>
      ))}
    </div>
  );
}

export default function AdsIntelligencePage() {
  const nav = useNavigate();
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [source, setSource] = useState("meta");
  const [includeCompetitors, setIncludeCompetitors] = useState(true);
  const [report, setReport] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    adsListBrands().then((b) => {
      const items = b.items || [];
      setBrands(items);
      if (items[0]) setBrandId(String(items[0].id));
    }).catch((e) => setErr(e.message));
  }, []);

  const loadLatest = useCallback(async (bid) => {
    if (!bid) return;
    setLoading(true); setErr(null); setReport(null);
    try {
      const r = await adsGetIntelligence(bid);
      if (r) { setReport(r.report); setGeneratedAt(r.created_at); }
      else { setGeneratedAt(null); }
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (brandId) loadLatest(brandId); }, [brandId, loadLatest]);

  const onGenerate = async () => {
    setGenerating(true); setErr(null);
    try {
      const r = await adsGenerateIntelligence({
        brand_id: Number(brandId), source, include_competitors: includeCompetitors,
      });
      if (r.status === "no_data") { setErr(r.message); setReport(null); }
      else { setReport(r); setGeneratedAt(new Date().toISOString()); }
    } catch (e) { setErr(e.message); } finally { setGenerating(false); }
  };

  const syn = report?.synthesis;
  const aggs = report?.aggregates;

  return (
    <div style={{ minHeight: "100vh", background: INK, color: "#e8e8ef", padding: "24px 32px" }}>
      <button onClick={() => nav("/ads")} style={btnGhost}>← Back to Ads</button>
      <h1 style={{ margin: "10px 0 2px", fontSize: 26, fontWeight: 900 }}>
        🧠 Intelligence <span style={{ color: GOLD }}>·</span> Visual Competitive Analysis
      </h1>
      <div style={{ color: "#8a90a6", fontSize: 13, marginBottom: 16 }}>
        Synthesized from the visual attributes extracted across your brand and competitors’ ads.
      </div>

      <div style={{ ...PanelStyle, padding: 16, marginBottom: 16, display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div>
          <label style={lbl}>Brand</label>
          <select style={input} value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Provider</label>
          <select style={input} value={source} onChange={(e) => setSource(e.target.value)}>
            <option value="meta">Meta</option>
            <option value="google">Google</option>
            <option value="tiktok">TikTok</option>
          </select>
        </div>
        <label style={{ ...checkRow, marginBottom: 8 }}>
          <input type="checkbox" checked={includeCompetitors} onChange={(e) => setIncludeCompetitors(e.target.checked)} />
          &nbsp;Include competitors
        </label>
        <button onClick={onGenerate} disabled={generating || !brandId} style={{ ...btnPrimary, opacity: generating ? 0.6 : 1 }}>
          {generating ? "Analyzing…" : "🧠 Generate Intelligence"}
        </button>
      </div>

      {err && <div style={errBox}>{err}</div>}
      {loading && <div style={{ color: "#8a90a6", padding: 20 }}>Loading latest report…</div>}

      {!loading && !report && !err && (
        <div style={{ ...PanelStyle, padding: 40, textAlign: "center", color: "#8a90a6" }}>
          No report yet. Click <b style={{ color: GOLD }}>Generate Intelligence</b> to analyze this brand’s competitive set.
        </div>
      )}

      {report && syn && (
        <>
          {generatedAt && (
            <div style={{ color: "#8a90a6", fontSize: 12, marginBottom: 8 }}>
              Generated {new Date(generatedAt).toLocaleString()} · model {report.model} · ${Number(report.cost_usd || 0).toFixed(4)}
            </div>
          )}

          {aggs?.coverage && (
            <div style={{ ...PanelStyle, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#cdd2e6" }}>
              <b style={{ color: GOLD }}>Coverage:</b> {aggs.coverage.advertisers} advertisers · {aggs.coverage.ads} ads ·{" "}
              {aggs.coverage.extracted}/{aggs.coverage.creatives} creatives extracted ({aggs.coverage.extraction_pct}%)
              {aggs.coverage.extraction_pct < 60 && <span style={{ color: "#f1c40f" }}> ⚠ low extraction coverage - results are directional</span>}
            </div>
          )}

          <Section title="Executive summary">
            <p style={{ fontSize: 14, lineHeight: 1.6, color: "#e8e8ef" }}>{syn.executive_summary}</p>
          </Section>

          {syn.trends && (
            <Section title={`📈 What changed${report.trends_vs ? ` since ${new Date(report.trends_vs).toLocaleDateString()}` : ""}`}>
              {syn.trends.summary && (
                <p style={{ fontSize: 14, lineHeight: 1.6, color: "#e8e8ef", marginTop: 0 }}>{syn.trends.summary}</p>
              )}
              {syn.trends.per_competitor?.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10, marginBottom: 8 }}>
                  {syn.trends.per_competitor.map((m, i) => (
                    <div key={i} style={cardStyle}>
                      <div style={{ fontWeight: 800, color: GOLD }}>{m.advertiser}</div>
                      {m.headline && <div style={{ fontSize: 13, color: "#cdd2e6", margin: "2px 0 4px" }}>{m.headline}</div>}
                      {m.shifts?.length > 0 && (
                        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12.5, color: "#cdd2e6" }}>
                          {m.shifts.map((s, j) => <li key={j}>{s}</li>)}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Column title="↗ Emerging angles" items={syn.trends.emerging_angles} tone="#2ecc71" />
                <Column title="↘ Fading angles" items={syn.trends.fading_angles} tone="#e67e22" />
              </div>
            </Section>
          )}

          {syn.per_competitor?.length > 0 && (
            <Section title="Per-competitor positioning">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
                {syn.per_competitor.map((c, i) => (
                  <div key={i} style={cardStyle}>
                    <div style={{ fontWeight: 800, color: GOLD, marginBottom: 4 }}>{c.advertiser}</div>
                    <div style={{ fontSize: 13, color: "#cdd2e6" }}>{c.positioning}</div>
                    {c.dominant_angle && <div style={{ fontSize: 12, color: "#8a90a6", marginTop: 4 }}>Dominant angle: <b style={{ color: "#cdd2e6" }}>{c.dominant_angle}</b></div>}
                    {c.notable?.length > 0 && (
                      <ul style={{ margin: "6px 0 0", paddingLeft: 16, fontSize: 12.5, color: "#cdd2e6" }}>
                        {c.notable.map((n, j) => <li key={j}>{n}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {syn.brand_vs_field && (
            <Section title="Your brand vs the field">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Column title="Where you align" items={syn.brand_vs_field.where_we_align} tone="#3498db" />
                <Column title="Where you differ" items={syn.brand_vs_field.where_we_differ} tone="#2ecc71" />
                <Column title="White space to own" items={syn.brand_vs_field.white_space} tone={GOLD} />
              </div>
            </Section>
          )}

          {syn.recommended_angles?.length > 0 && (
            <Section title="Recommended creative angles">
              {syn.recommended_angles.map((a, i) => (
                <div key={i} style={{ ...cardStyle, marginBottom: 8 }}>
                  <div style={{ fontWeight: 800, color: "#e8e8ef" }}>→ {a.angle}</div>
                  <div style={{ fontSize: 12.5, color: "#8a90a6", marginTop: 2 }}>{a.rationale}</div>
                </div>
              ))}
            </Section>
          )}

          {syn.data_caveats?.length > 0 && (
            <div style={{ fontSize: 12, color: "#8a90a6", margin: "8px 0 16px" }}>
              ⚠ {syn.data_caveats.join(" · ")}
            </div>
          )}

          {aggs?.advertisers && (
            <Section title="Distributions [exact]">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
                {Object.entries(aggs.advertisers).map(([adv, block]) => (
                  <div key={adv} style={cardStyle}>
                    <div style={{ fontWeight: 800, color: GOLD, marginBottom: 6 }}>
                      {adv}{block.is_self ? " (you)" : ""} · {block.creatives_extracted} creatives
                      {block.low_confidence && <span style={{ color: "#f1c40f", fontSize: 11 }}> ⚠ low-N</span>}
                    </div>
                    <DistTable title="Funnel stage" dist={block.dimensions?.funnel_stage} />
                    <DistTable title="Claim types" dist={block.dimensions?.claim_types} />
                    <DistTable title="Palette" dist={block.dimensions?.color_palette} />
                    <DistTable title="Persuasion" dist={block.dimensions?.persuasion_pattern} />
                  </div>
                ))}
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ ...PanelStyle, padding: 16, marginBottom: 14 }}>
      <h3 style={{ margin: "0 0 10px", fontSize: 15, color: GOLD }}>{title}</h3>
      {children}
    </div>
  );
}

function Column({ title, items, tone }) {
  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 12, fontWeight: 800, color: tone, marginBottom: 6, textTransform: "uppercase" }}>{title}</div>
      {(items || []).length === 0 ? <div style={{ color: "#8a90a6", fontSize: 12 }}>-</div> : (
        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12.5, color: "#cdd2e6" }}>
          {items.map((it, i) => <li key={i} style={{ marginBottom: 3 }}>{it}</li>)}
        </ul>
      )}
    </div>
  );
}

const PanelStyle = { background: PANEL, borderRadius: 12, border: "1px solid #1e2440" };
const cardStyle = { background: "#0d1226", border: "1px solid #1e2440", borderRadius: 8, padding: "10px 12px" };
const lbl = { display: "block", margin: "0 0 4px", fontSize: 12, color: "#8a90a6" };
const input = { padding: "8px 10px", borderRadius: 8, border: "1px solid #2a3158", background: INK, color: "#e8e8ef", minWidth: 160 };
const checkRow = { display: "flex", alignItems: "center", fontSize: 13, color: "#cdd2e6" };
const btnPrimary = { padding: "9px 16px", borderRadius: 8, border: `1px solid ${GOLD}`, background: GOLD, color: INK, fontWeight: 700, cursor: "pointer" };
const btnGhost = { padding: "7px 12px", borderRadius: 8, border: "1px solid #2a3158", background: "transparent", color: "#cdd2e6", cursor: "pointer" };
const errBox = { background: "#3a1620", border: "1px solid #e74c3c", color: "#ffb3bd", padding: "8px 12px", borderRadius: 8, margin: "8px 0", fontSize: 13 };
